import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DraftEmail } from 'src/app/models/server/draft-email';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { ViewsStateService } from '../state/views-state.service';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class DraftEmailsService extends ServiceBase {
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService,
        public viewsStateService: ViewsStateService
    ) {
        super(connectedUserStateService);
    }

    /**
     * Liste les brouillons de mail d'un dossier.
     * @param viewId : Id de la vue.
     * @param folderId : Id du dossier
     * @returns Liste de brouillon de mails.
     */
    public list(viewId: number, folderId: string): Observable<DraftEmail[]> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http.get<DraftEmail[]>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/draft-emails`, httpOptions).pipe(
            map((x) => {
                return DraftEmail.fromArray(x);
            })
        );
    }

    /**
     * Retourne un brouillon de mail.
     * @param viewId : Id de la vue.
     * @param folderId : Id du dossier
     * @param draftEmailId : Id du brouillon de mail.
     * @returns Un brouillon de mail.
     */
    public get(viewId: number, folderId: string, draftEmailId: number): Observable<DraftEmail> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .get<DraftEmail>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/draft-emails/${draftEmailId}`, httpOptions)
            .pipe(
                map((x) => {
                    return DraftEmail.from(x);
                })
            );
    }

    /**
     * Ajoute un brouillon de mail.
     * @param viewId Id de la vue à laquelle appartient le dossier.
     * @param folderId Id du dossier.
     * @param draftEmail : Instance du brouillon de mail.
     * @returns : L'instance du brouillon de mail créé.
     */
    public add(viewId: number, folderId: string, draftEmail: DraftEmail): Observable<DraftEmail> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        draftEmail.organizationId = this.organizationId;
        draftEmail.viewId = viewId;
        draftEmail.folderId = folderId;

        return this.http
            .post<DraftEmail>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/draft-emails`, draftEmail, httpOptions)
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Met à jour un brouillon de mail.
     * @param viewId Id de la vue à laquelle appartient le dossier.
     * @param folderId Id du dossier.
     * @param reminder : Instance du brouillon de mail.
     * @returns : L'instance du brouillon de mail modifié.
     */
    public update(viewId: number, folderId: string, draftEmail: DraftEmail): Observable<DraftEmail> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        draftEmail.organizationId = this.organizationId;
        draftEmail.viewId = viewId;
        draftEmail.folderId = folderId;

        return this.http
            .put<DraftEmail>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/draft-emails/${draftEmail.id}`, draftEmail, httpOptions)
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Supprime un brouillon de mail.
     * @param viewId : Id de la vue.
     * @param draftEmail : Instance du brouillon de mail.
     */
    public delete(viewId: number, draftEmail: DraftEmail): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .delete<boolean>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${draftEmail.folderId}/draft-emails/${draftEmail.id}`, httpOptions)
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Récupère le fichier d'une pièce-jointe d'un brouillon de mail.
     * @param viewId : Id de la vue
     * @param folderId Id du dossier
     * @param draftEmailId : Id du brouillon de mail
     * @param attachmentId : Id de la pièce-jointe
     * @returns : Le fichier de la pièce-jointe.
     */
    public getAttachmentFile(viewId: number, folderId: string, draftEmailId: number, attachmentId: string): Observable<Blob> {
        const httpOptions = {
            headers: new HttpHeaders({}),
            responseType: 'blob' as 'blob',
        };

        // Appel de la Web Api
        return this.http
            .get(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/draft-emails/${draftEmailId}/attachments/${attachmentId}`,
                httpOptions
            )
            .pipe(
                map((x: Blob) => {
                    return x;
                })
            );
    }
}
